import { ContextLabelOutputV1, sqContextApi } from '@/sdk';
import { LabelOutputV1 } from 'sdk/model/LabelOutputV1';
import { LabelCategoryOutputV1 } from 'sdk/model/LabelCategoryOutputV1';
import { randomUUID } from '@/utilities/utilities';
import * as graphql from '@/vantage/graphql';
import { ensureDate } from '@/vantage/utilities';
import _ from 'lodash';

interface LabelSearchParams {
  categoryId: string;
  name: string;
  parentId?: string;
}

interface LabelsSearchParams {
  categoryId: string;
  parentId?: string;
}

function filterByParentId<T extends { parentId?: string }>(inputs: T[], parentId?: string): T[] {
  // When parentId is undefined, all labels/categories are returned, so we need to manually filter on parentId to
  // get the root label/category
  return inputs.filter((input) => (_.isNil(input.parentId) && _.isNil(parentId)) || input.parentId === parentId);
}

export async function findLabels({ categoryId, parentId }: LabelsSearchParams): Promise<LabelOutputV1[]> {
  const {
    data: { labels },
  } = await sqContextApi.findLabels({ categoryId, parentId });
  return filterByParentId(labels ?? [], parentId);
}

/** @exportedfortesting */
export async function findLabelByName({ categoryId, name, parentId }: LabelSearchParams) {
  const labels = await findLabels({ categoryId, parentId });
  return labels.find((label) => label.name === name);
}

export const findOrCreateLabel = async (
  params: LabelSearchParams,
  retry = false,
): Promise<LabelOutputV1 | undefined> => {
  const label = await findLabelByName(params);
  if (label) {
    return label;
  } else {
    try {
      return await sqContextApi.createLabel({ ...params, icon: undefined }).then(({ data }) => data);
    } catch (e) {
      // Create fails if label was created during attempt to fetch label.
      if (retry) {
        return findOrCreateLabel(params);
      } else {
        throw e;
      }
    }
  }
};

export async function findCategory(name: string, parentId: string | undefined = undefined) {
  const {
    data: { categories },
  } = await sqContextApi.findLabelCategories({ parentId });
  return filterByParentId(categories ?? [], parentId).find((category) => category.name === name);
}

export async function findOrCreateLabelCategory(name: string, parentId: string | undefined = undefined) {
  const category = await findCategory(name, parentId);
  if (category) {
    return category;
  }
  const { data } = await sqContextApi.createLabelCategory({ name });
  return data;
}

export async function findCategoryHierarchy(categoryNames: string[]) {
  const categories: LabelCategoryOutputV1[] = [];
  let category: LabelCategoryOutputV1 | undefined;
  let categoryName: string | undefined;
  while (categoryNames.length > 0) {
    [categoryName, ...categoryNames] = categoryNames;
    category = await findCategory(categoryName, category?.id);
    if (category) {
      categories.push(category);
    } else {
      break;
    }
  }
  return categories;
}

interface ContextLabelFromNames {
  itemId: string;
  datumId: string;
  categories: any[];
  labelNames: string[];
  start: string;
  end: string;
  metadata?: Record<string, any>;
}

interface ContextLabel {
  itemId: string;
  datumId: string;
  label?: LabelOutputV1;
  start: string;
  end: string;
}

async function createContextLabelSafe({
  itemId,
  datumId,
  label,
  start,
  end,
}: ContextLabel): Promise<ContextLabelOutputV1> {
  try {
    const { data: contextLabel } = await sqContextApi.createContextLabel(
      { datumId, labelId: label?.id, startTime: start, endTime: end },
      { itemId },
    );
    return contextLabel;
  } catch (e: any) {
    // If the context label exists, likely by someone manually creating it without metadata, use it.
    if (e.response?.status === 400 && e.response.data.statusMessage.includes('already exists')) {
      console.warn('Attempted to create a context that already exists. Using existing context instead.');
      const { labels: contextLabels } = await graphql.labelContextForIdPairs([[itemId, datumId]]);
      const contextLabel = contextLabels.find(
        (ctx) =>
          ctx.context === label?.id &&
          ensureDate(ctx.startTime)?.getTime() === ensureDate(start)?.getTime() &&
          ensureDate(ctx.endTime)?.getTime() === ensureDate(end)?.getTime() &&
          ctx.archived === false,
      );
      return contextLabel as ContextLabelOutputV1;
    } else {
      throw e;
    }
  }
}

export async function add({ itemId, datumId, categories, labelNames, start, end, metadata }: ContextLabelFromNames) {
  let label: LabelOutputV1 | undefined;
  let category, labelName;
  while (labelNames.length > 0) {
    [category, ...categories] = categories;
    [labelName, ...labelNames] = labelNames;
    label = await findOrCreateLabel({ categoryId: category.id, name: labelName, parentId: label?.id });
    const contextLabel = await createContextLabelSafe({ itemId, datumId, label, start, end });
    await sqContextApi.createContextOpaque(
      {
        datumId: datumId ?? undefined,
        key: `vantage_metadata${randomUUID()}`,
        data: JSON.stringify({ ...metadata, category, labelName, seeq_context_ids: [contextLabel.contextId] }),
        startTime: start ?? undefined,
        endTime: end ?? undefined,
      },
      {
        itemId,
      },
    );
  }
}
